export function formatUSD(value: number | null | undefined, opts?: { compact?: boolean }): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const compact = opts?.compact ?? false;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : 2,
    minimumFractionDigits: compact ? 0 : 2,
  }).format(value);
}

export function formatNumber(
  value: number | null | undefined,
  opts?: { compact?: boolean; fractionDigits?: number },
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const fd = opts?.fractionDigits;
  return new Intl.NumberFormat('en-US', {
    notation: opts?.compact ? 'compact' : 'standard',
    maximumFractionDigits: fd ?? (opts?.compact ? 1 : 0),
    minimumFractionDigits: fd,
  }).format(value);
}

export function formatPct(value: number | null | undefined, opts?: { fractionDigits?: number }): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const fd = opts?.fractionDigits ?? 1;
  return `${(value * 100).toFixed(fd)}%`;
}

/**
 * Effective per-share price for a trade row: `usdc / size`. Preferred over
 * the raw `price` field returned by Polymarket, which is often the LIMIT
 * price of the order rather than the fill price — so `size * price` does
 * not always equal `usdc_size` (see Questions and Answers.md Q1). This
 * derivation is always internally consistent with the reported USDC and
 * share counts.
 *
 * Returns '—' when the inputs can't produce a meaningful price:
 *   - non-TRADE activity types (SPLIT / MERGE / REDEEM / CONVERSION /
 *     REWARD) — callers should pass `size = null` in that case
 *   - missing usdc or size
 *   - size = 0 (would divide by zero)
 */
export function formatEffectivePrice(
  usdc: number | string | null | undefined,
  size: number | string | null | undefined,
  opts?: { fractionDigits?: number },
): string {
  const u = usdc === null || usdc === undefined ? NaN : Number(usdc);
  const s = size === null || size === undefined ? NaN : Number(size);
  if (Number.isNaN(u) || Number.isNaN(s) || s === 0) return '—';
  return (u / s).toFixed(opts?.fractionDigits ?? 3);
}

export function formatAddress(address: string | null | undefined): string {
  if (!address) return '—';
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

/**
 * Wallet display label. Resolution order:
 *
 *   1. display_name (user-editable, set by the wallet holder on Polymarket)
 *   2. pseudonym (system-assigned dictionary slug like "Timely-Federation")
 *   3. truncated address ("0x1234…abcd") when neither is present
 *
 * Either of the first two can also be the full 42-char Ethereum address —
 * a legacy data shape where the address got written into the label column
 * because no real name was ever assigned. Those are detected and treated
 * as "no label" so we don't render a giant hex string in tables.
 *
 * Use this everywhere a wallet label is rendered. `display_name ?? pseudonym ?? address`
 * is subtly wrong because it leaves empty strings and address-stuffed
 * labels through.
 */
export function formatWalletLabel(
  displayName: string | null | undefined,
  pseudonym: string | null | undefined,
  address: string | null | undefined,
): string {
  const isUsableLabel = (s: string | null | undefined): string | null => {
    const trimmed = s?.trim();
    if (!trimmed) return null;
    // Any 0x-prefixed pure-hex string is address-shaped junk, not a
    // real user name. Rejected regardless of length — the DB has both
    // full 42-char addresses (1,083 of them), truncated variants
    // (41-char: 151, 40-char: 20), and shorter fragments. None of
    // these are legitimate display names.
    if (/^0x[0-9a-fA-F]+$/.test(trimmed)) return null;
    // Polymarket's /activity `name` field frequently comes back as a
    // checksummed address followed by a hyphen + numeric suffix (unix ms or
    // similar). Corpus-wide check on 2026-07-06 found 171k wallets with this
    // exact shape stored as display_name — none are user-set names. Reject.
    if (/^0x[0-9a-fA-F]+-\d+$/.test(trimmed)) return null;
    return trimmed;
  };
  return (
    isUsableLabel(displayName)
    ?? isUsableLabel(pseudonym)
    ?? formatAddress(address)
  );
}

/**
 * All date/time formatters render in the browser's local timezone, read
 * automatically from the OS via Intl. `timeZoneName: 'short'` appends the
 * matching abbreviation (PT, ET, BST, JST, …) so a value is never ambiguous.
 *
 * For an explicit UTC rendering, use `formatDateUTC` / `formatDateTimeUTC`.
 */
const dateOpts: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
};
const dateTimeOpts: Intl.DateTimeFormatOptions = {
  ...dateOpts,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZoneName: 'short',
};

export function formatDate(value: string | number | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, dateOpts).format(d);
}

/** `timeZone` (an IANA name) renders the value as if the reader were in that
 *  zone, abbreviation and all — the same output their machine would give if
 *  it were set there. Undefined keeps the viewer's own zone. Report mode
 *  passes 'America/New_York' so a screenshot cannot mix zones: a chart axis
 *  in ET beside a resolution timestamp in MDT is the kind of detail that
 *  quietly misleads a reader of the finished document. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  opts?: { timeZone?: string | null },
): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(
    undefined,
    opts?.timeZone ? { ...dateTimeOpts, timeZone: opts.timeZone } : dateTimeOpts,
  ).format(d);
}

export function formatDateUTC(value: string | number | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { ...dateOpts, timeZone: 'UTC' }).format(d);
}

export function formatDateTimeUTC(value: string | number | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { ...dateTimeOpts, timeZone: 'UTC' }).format(d);
}

/**
 * Render a two-timestamp window. If both timestamps fall on the same local
 * day, the date and timezone are only printed once and the end timestamp is
 * just the time (e.g. "Jun 02, 2026, 6:00 PM – 7:00 PM MDT"). If they cross
 * a day boundary, the full datetime is printed on both sides.
 */
export function formatDateTimeRange(
  fromValue: string | number | Date | null | undefined,
  toValue: string | number | Date | null | undefined,
): string {
  if (!fromValue || !toValue) return '—';
  const from = new Date(fromValue);
  const to = new Date(toValue);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '—';
  const sameDay =
    from.getFullYear() === to.getFullYear() &&
    from.getMonth() === to.getMonth() &&
    from.getDate() === to.getDate();
  if (!sameDay) {
    return `${formatDateTime(from)} – ${formatDateTime(to)}`;
  }
  // Same day: render full datetime on left, just the time on right, and
  // append the timezone abbreviation once (extracted from the left side).
  // We walk the parts, drop the timezone token AND the separator literal
  // immediately before it, and preserve every other literal so the natural
  // " " between "Jun" and "03" or "3:00" and "AM" stays intact.
  const leftParts = new Intl.DateTimeFormat(undefined, dateTimeOpts).formatToParts(from);
  const tz = leftParts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  const kept: typeof leftParts = [];
  for (let i = 0; i < leftParts.length; i++) {
    const p = leftParts[i];
    if (p.type === 'timeZoneName') continue;
    // If the NEXT non-empty token is the timezone, this literal is the
    // separator (" ") sitting between time and TZ — drop it too.
    if (p.type === 'literal' && leftParts[i + 1]?.type === 'timeZoneName') continue;
    kept.push(p);
  }
  const leftWithoutTz = kept.map((p) => p.value).join('').trimEnd();
  const rightTime = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(to);
  return `${leftWithoutTz} – ${rightTime}${tz ? ` ${tz}` : ''}`;
}

export function formatRelative(value: string | number | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value).getTime();
  if (Number.isNaN(d)) return '—';
  const diff = Date.now() - d;
  const abs = Math.abs(diff);
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;
  const sign = diff >= 0 ? 'ago' : 'from now';
  let n: number;
  let unit: string;
  if (abs < min) {
    return 'just now';
  } else if (abs < hr) {
    n = Math.round(abs / min);
    unit = 'min';
  } else if (abs < day) {
    n = Math.round(abs / hr);
    unit = 'hr';
  } else if (abs < week) {
    n = Math.round(abs / day);
    unit = 'day';
  } else if (abs < month) {
    n = Math.round(abs / week);
    unit = 'wk';
  } else if (abs < year) {
    n = Math.round(abs / month);
    unit = 'mo';
  } else {
    n = Math.round(abs / year);
    unit = 'yr';
  }
  return `${n} ${unit}${n === 1 ? '' : 's'} ${sign}`;
}

export function classNames(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Does a filter query mean "this condition_id"?
 *
 * A condition_id is 66 characters of hex, so ANY short hex run matches a fair
 * number of them by coincidence. Reported 2026-09-25: typing `ffff` into the
 * scope filter returned matches — 2 of the 3,586 markets in that scope had
 * `ffff` somewhere inside their cid, and none of them had it in the question.
 * Every one of those hits was true and useless.
 *
 * So a cid is only searched when the query is unambiguously addressing one:
 * it starts with `0x`. Cids are always rendered and copied with that prefix, so
 * pasting one — whole or truncated — still works, and no English or hex-looking
 * word can collide with it.
 */
export function matchesConditionId(conditionId: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q.startsWith('0x') || q.length < 3) return false;
  return conditionId.toLowerCase().includes(q);
}
